import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'styled-components';
import { ParallaxProvider } from 'react-scroll-parallax';
import cn from 'classnames';
import classNames from 'classnames';

import { Global } from '@common/components/Global/Global';
import Message from '@common/components/Message/Message';
import { Disclaimers } from '@common/components/Paragraph/components/Disclaimers/Disclaimers';
import i18n from '@components/i18n/i18n';
import FooterComponent from '@shcc/components/FooterComponent/FooterComponent';
import theme from '@theme/theme';
import { useHeaderFooter } from '@hooks';
// Navigation dependencies
import MainNavigationComponent from '@shcc/components/navigations/MainNavigationComponent/MainNavigationComponent';
import RegionComponent from '@shcc/components/RegionComponent/RegionComponent';
import styles from '@shcc/layouts/MainLayout/MainLayout.module.scss';
import stylesMenu from '@shcc/overlays/OverlaySidebar/OverlaySidebar.module.scss';
import { REGION_OVERLAYS } from '@shcc/constants';
// // superscripts
import { getTaxonomyLabels } from '@utils';

let regions = {};
regions[REGION_OVERLAYS] = React.createRef();
function getRegion(name) {
  return regions[name] && regions[name].current ? regions[name].current : null;
}

const Main = props => {
  const {
    type,
    location,
    loggedIn,
    hideReserve,
    invertibleLinkTheme,
    direction,
    backgroundColor,
    children,
  } = props;
  const [menuIntervalId, setMenuIntervalId] = useState(false);

  const checkMenu = useCallback(
    active => {
      const scrollContainerComponentQuery = document.querySelectorAll(
        '[class*="ScrollContainerComponent-module--content"]'
      );
      let scrollContainer;
      if (scrollContainerComponentQuery.length) {
        scrollContainer = scrollContainerComponentQuery[0];
      }
      if (active) {
        if (menuIntervalId) return;
        setMenuIntervalId(
          setInterval(function () {
            if (document?.hasFocus()) {
              const menu = document.getElementsByClassName(
                stylesMenu.content
              ).length;
              // if (body) {
              // body.style = {
              //   ...body.style,
              //   maxHeight: menu ? '100vh' : null,
              //   overflowY: menu ? 'hidden' : null
              // }
              // }

              if (scrollContainer) {
                scrollContainer.style.height = menu ? '100vh' : null;
                scrollContainer.styleoverflowY = menu ? 'scroll' : null;
              }
            }
          }, 750)
        );
      } else {
        clearInterval(menuIntervalId);
        // if (body) {
        //   body.style = {
        //     ...body.style,
        //     maxHeight: null,
        //     overflowY: null
        //   }
        // }

        if (scrollContainer) {
          scrollContainer.style.height = null;
          scrollContainer.style.overflow = null;
        }
        setMenuIntervalId(null);
      }
    },
    [menuIntervalId]
  );

  useEffect(() => {
    checkMenu(true);
    if (typeof window !== `undefined` && typeof document !== `undefined`) {
      window.scrollTo(0, 0);
      document.querySelector('body').style.height = '';
    }

    return () => {
      checkMenu(false);
    };
  }, []);

  return (
    <div>
      <div className={styles.pages} style={{ zIndex: 1 }}>
        <MainNavigationComponent
          className={classNames([styles.navigation, styles.navigationMain])}
          getRegion={getRegion}
          hideReserve={hideReserve}
          invertibleLinkTheme={invertibleLinkTheme}
          location={location}
          loggedIn={loggedIn}
          type={type}
        />
      </div>
      <div>
        <ParallaxProvider>
          <main style={{ backgroundColor }} dir={direction}>
            {children}
          </main>
        </ParallaxProvider>
      </div>
      <div className={styles.overlays}>
        <RegionComponent
          name={REGION_OVERLAYS}
          ref={regions[REGION_OVERLAYS]}
          getRegion={getRegion}
        />
      </div>
    </div>
  );
};

export const Layout = props => {
  const {
    bodyClass,
    children,
    theme: pageTheme,
    hideReserve,
    hideFooter,
    location,
    backgroundColor,
    direction,
    type,
    invertibleLinkTheme = false,
    pageData = {},
  } = props;

  const mainDir = direction || i18n.dir();
  const { copyrightBlock, privacyBlock, socialLinksBlock, getMenuLinks } =
    useHeaderFooter();

  const getDisclaimers = sections => {
    const disclaimers = sections.find(
      section => section.type === 'paragraph__disclaimers'
    );
    let filtered = disclaimers.data.find(
      translation => translation.langcode === i18n.language
    );
    // if disclaimers are not set in the current language, use english
    if (!filtered)
      filtered = disclaimers.data.find(
        translation => translation.langcode === 'en'
      );
    return filtered;
  };

  const label = getTaxonomyLabels('disclaimers')['section-disclaimer-title'];
  const disclaimers = pageData.sections
    ? getDisclaimers(pageData.sections)
    : {};

  const [messageVisible, setMessageVisible] = useState(false);
  const [messageStyle, setMessageStyle] = useState(null);
  const [messageContent, setMessageContent] = useState({});

  const updateMessage = (visibility, content, style) => {
    if (content) setMessageContent(() => content);
    if (style) setMessageStyle(() => style);
    setMessageVisible(() => visibility);
  };

  const dispatch = {
    message: updateMessage,
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Helmet */}
      <Helmet
        bodyAttributes={{
          class: cn(bodyClass, pageTheme && `page-${pageTheme}`),
        }}
      >
        <html dir={i18n.dir()} />
      </Helmet>
      {/* Global */}
      <Global />
      {/* Main */}
      <Main
        hideReserve={hideReserve}
        backgroundColor={backgroundColor}
        location={location}
        direction={mainDir}
        type={type}
        invertibleLinkTheme={invertibleLinkTheme}
      >
        {children}
      </Main>
      {/* Footer (hide only for Configurator) */}
      {hideFooter ? (
        false
      ) : (
        <FooterComponent
          linksFirst={getMenuLinks('footer-first-en')}
          linksSecond={getMenuLinks('footer-second-en')}
          linksSocial={socialLinksBlock}
          copyrightBlock={copyrightBlock}
          privacyBlock={privacyBlock}
          location={location}
        />
      )}
      {Object.keys(disclaimers).length > 0 && (
        <Disclaimers
          title={label}
          paragraph={disclaimers}
          dispatch={dispatch}
        />
      )}
      <Message
        dispatch={dispatch}
        ref={{
          visibility: messageVisible,
          content: messageContent,
          style: messageStyle,
        }}
      />
    </ThemeProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  bodyClass: PropTypes.string,
  theme: PropTypes.string,
  hideReserve: PropTypes.bool,
  location: PropTypes.shape({
    href: PropTypes.string,
    origin: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  backgroundColor: PropTypes.string,
  hideFooter: PropTypes.bool,
  direction: PropTypes.string,
};

Layout.defaultProps = {
  bodyClass: 'page',
  hideReserve: false,
  backgroundColor: 'transparent',
  hideFooter: false,
};
